/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState } from "react";
import { CardContent, TextField, FormControlLabel, Checkbox, Divider } from "@material-ui/core";
import { v4 as uuid } from "uuid";
import { useStatusAlerts } from "../../context/statusAlerts";
import { EmptyPlaceholder, InputTooltip } from "../../../../components";
import { ActionButtons } from "../../../../components/Common";
import { Autocomplete } from "@material-ui/lab";
import { AGENT_STATUS_ALERTS } from "../../../../utility/constants";
import { AGENT_STATUS_ALERT_TOOLTIPS } from "../../../../utility/toolTipMessages";
import { AlertsList } from "./AlertsList";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { errorToObject, v2 } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";

const COMPONENT_NAME = "Admin StatusAlertsManagement/components/AgentStatus/AgentStatusSettings";

/**
 * This component displays the details of alerts and ability to add alerts configured for selected status.
 *
 * @param {Array} selectedStatusAlertId
 * @param {Boolean} isFormChanged A boolean value denoting whether or not the form has been edited.
 * @param {Boolean} validationState A boolean indicating if there are invalid settings.
 * @param {Function} onCancel function to clear details of active status.
 */
export const AgentStatusSettings = ({ selectedStatusId, isFormChanged = false, validationState, onCancel }) => {
	const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
	const { removeStatusAlert, statusAlerts, updateSingleStatusAlert, statusLookup, loading, saveStatusAlertsList } =
		useStatusAlerts();

	const selectedStatus = statusAlerts.find((status) => status.id === selectedStatusId);
	const alertsCount = selectedStatus?.alerts ? selectedStatus?.alerts.length : 0;

	const setStatusName = (_event, status) => {
		const updatedValues = {
			statusName: status ? status.Name : "",
			statusId: status ? status.Id : "",
		};
		updateSingleStatusAlert({ ...selectedStatus, ...updatedValues });
	};
	const updateMessage = (event, alertId) => {
		const message = String(event.currentTarget.value).trimLeft();
		let newStatusAlert = { ...selectedStatus };
		newStatusAlert.alerts = newStatusAlert.alerts.map((alert) =>
			alert.id === alertId ? { ...alert, message } : alert,
		);
		updateSingleStatusAlert({ ...newStatusAlert });
	};
	const updateTime = (event, alertId) => {
		const time = event.currentTarget.valueAsNumber;
		const clonedStatuses = { ...selectedStatus };
		clonedStatuses.alerts = clonedStatuses.alerts?.map((alert) => ({
			...alert,
			time: alert.id === alertId ? time : alert.time,
		}));

		clonedStatuses.alerts = clonedStatuses?.alerts?.map((a) => {
			const duplicateTimeAlert = clonedStatuses?.alerts?.find((b) => b.id !== a.id && b.time === a.time);
			let timeError = "";
			if (!Number.isSafeInteger(a.time)) {
				timeError = "Only integers allowed.";
			} else if (duplicateTimeAlert) {
				timeError = "Duplicate alert time.";
			} else if (a.time > AGENT_STATUS_ALERTS.ALERT_MAX_TIME) {
				timeError = `Cannot be more than ${AGENT_STATUS_ALERTS.ALERT_MAX_TIME} minutes.`;
			}
			return { ...a, error: timeError };
		});

		updateSingleStatusAlert({ ...clonedStatuses });
	};
	const handleAddAlert = () => {
		const newAlert = { id: uuid(), time: "", message: "" };
		const newStatusAlert = { ...selectedStatus };
		newStatusAlert.alerts.unshift(newAlert);
		updateSingleStatusAlert({ ...newStatusAlert });
	};
	const deleteAlertMessage = (alertId) => {
		const newStatusAlert = { ...selectedStatus };
		newStatusAlert.alerts = newStatusAlert.alerts.filter((alert) => alert.id !== alertId);
		updateSingleStatusAlert({ ...newStatusAlert });
	};
	const handleActiveChange = (event) => {
		const newStatusAlert = { ...selectedStatus };
		newStatusAlert.active = !event.target.checked;
		updateSingleStatusAlert({ ...newStatusAlert });
	};

	const filteredStatusLookup = statusLookup.map((status) => ({
		...status,
		isConfigured: statusAlerts.find((alert) => alert.statusId === status.Id) !== undefined,
	}));

	return (
		<CardContent>
			{!selectedStatus ? (
				<EmptyPlaceholder message="To get started, select a status from the list on the left, or click the 'Add' button to create a new status alert." />
			) : (
				<>
					<div className="d-flex align-items-center justify-content-between flex-wrap">
						<h5 className="font-weight-bold m-0">{selectedStatus.new ? "Add Status Alert" : "Edit Status Alerts"}</h5>
						<ActionButtons
							showCancel
							showRemove
							showSave={isFormChanged}
							onCancel={onCancel}
							onRemove={() => setRemoveConfirmOpen(true)}
							onSave={() => saveStatusAlertsList(selectedStatusId)}
							disableSave={!validationState || loading || !alertsCount}
						/>
					</div>
					<div className="mt-4 d-flex align-items-center justify-content-between">
						<Autocomplete
							options={filteredStatusLookup}
							getOptionLabel={(option) => option.statusName}
							getOptionDisabled={(option) => option.isConfigured}
							getOptionSelected={(option, value) => option.Id === value.statusId}
							onChange={setStatusName}
							required
							value={selectedStatus?.statusName ? selectedStatus : null}
							className="col-6 px-0 mr-3"
							renderInput={(params) => (
								<TextField
									{...params}
									label="Status"
									variant="standard"
									title={selectedStatus.statusName ? "" : "Please select status."}
									error={!selectedStatus.statusName}
									helperText={!selectedStatus.statusName ? "Please select status" : ""}
								/>
							)}
						/>
						<FormControlLabel
							value="top"
							style={{ margin: "auto 0" }}
							control={
								<Checkbox
									checked={!selectedStatus?.active}
									color="secondary"
									size="small"
									onChange={handleActiveChange}
								/>
							}
							label={
								<span className="d-flex align-items-center" style={{ fontSize: 14, marginLeft: -6 }}>
									<span className="pr-2">Disable</span>
									<InputTooltip title={AGENT_STATUS_ALERT_TOOLTIPS.ALERT_DISABLE_HINT_TOOLTIP} />
								</span>
							}
						/>
					</div>
					<Divider className="mt-4 mb-2" />
					<AlertsList
						alertsCount={alertsCount}
						addAlertHandler={handleAddAlert}
						alerts={selectedStatus?.alerts}
						formEdited={false}
						removeAlertHandler={deleteAlertMessage}
						updateTime={updateTime}
						updateMessage={updateMessage}
					/>
					<ConfirmDialog
						title={"Remove Status Alerts?"}
						message={"Are you sure you want to delete all the alerts for this status?"}
						confirmOpen={removeConfirmOpen}
						onClose={async (confirmed) => {
							try {
								if (confirmed) {
									await removeStatusAlert(selectedStatusId);
								}
								setRemoveConfirmOpen(false);
							} catch (error) {
								v2.error(`Error while attempting to delete status alerts.`, {
									additionalData: { error: errorToObject(error) },
									componentName: COMPONENT_NAME,
									logCode: errorCodes.REMOVE_AGENT_STATUSES_ERROR,
								});
							}
						}}
					/>
				</>
			)}
		</CardContent>
	);
};
